/**
 * Application Entry Point
 * @description Bootstraps the Vue 3 application with Pinia and Vue Router
 */

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import OktaVue from '@okta/okta-vue'
import router from './router'
import App from './App.vue'
import oktaAuth from './config/okta'

// Import global styles
import './assets/main.css'

// Enable WebSocket debugging in development
if (import.meta.env.DEV) {
  import('./utils/debugWebSocket').then(({ enableWebSocketDebug }) => {
    enableWebSocketDebug()
    console.debug('[Main] WebSocket debugging enabled for console connections')
  })
}

/**
 * Create Vue application instance
 */
const app = createApp(App)

/**
 * Install Okta Vue Plugin FIRST
 * Provides useAuth composable and authentication state management
 * MUST be installed before Pinia to allow stores to use useOktaAuth()
 */
app.use(OktaVue, {
  oktaAuth,
  onAuthRequired: () => {
    // Redirect to Okta login when authentication is required
    oktaAuth.signInWithRedirect()
  },
  onAuthResume: () => {
    // Handle auth resume after redirect
    router.push({ name: 'dashboard' })
  }
})

/**
 * Install Pinia for state management
 * MUST be installed after OktaVue so auth store can use useOktaAuth()
 */
const pinia = createPinia()
app.use(pinia)

/**
 * Install Vue Router
 */
app.use(router)

/**
 * Make auth store globally accessible for useConsole composable
 * This allows useConsole to access the current JWT token without circular dependencies
 */
import { useAuthStore } from './stores/auth'
const authStore = useAuthStore()
window.__authStore = authStore

/**
 * Mount application to DOM
 */
app.mount('#app')

/**
 * Global error handler for production
 */
if (import.meta.env.PROD) {
  app.config.errorHandler = (err, instance, info) => {
    console.error('Global error:', err)
    console.error('Error info:', info)
    // TODO: Send to error tracking service in production
  }
}

/**
 * Performance monitoring (optional)
 */
if (import.meta.env.DEV) {
  app.config.performance = true
}
